import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import { FileTextOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { createReport, getMeta, invoiceReports, listReports, saveSettings } from "./api";
import { ROUNDING_OPTIONS } from "./calc";
import {
  STATUS_COLORS,
  centsToEuro,
  decimalInputProps,
  euroToCents,
  formatDay,
  formatEuro,
  formatMinutes,
  statusLabels
} from "./format";
import { PendingTag } from "../../../offline/OfflineNotice";
import "./workReports.css";

const BILLABLE = ["signed", "refused"];

const NewReportModal = ({ open, meta, onClose }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) form.setFieldsValue({ date: dayjs(), project: undefined, client: undefined });
  }, [open]);

  const submit = async values => {
    setBusy(true);
    try {
      const client = meta.clients.find(c => c._id === values.client);
      const res = await createReport({
        date: values.date.format("YYYY-MM-DD"),
        project: values.project || null,
        ...(values.client ? { client: values.client } : {}),
        ...(client?.address ? { siteAddress: client.address } : {})
      });
      history.push(`/user/work-reports/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="work-reports-page"
      open={open}
      title={t("New work report")}
      okText={t("Create")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="date" label={t("Date")} rules={[{ required: true }]}>
          <DatePicker className="wr-full" format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
        <Form.Item name="project" label={t("Project")}>
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            options={(meta?.projects || []).map(p => ({ value: p._id, label: p.name }))}
            onChange={value => {
              const project = meta.projects.find(p => p._id === value);
              if (project?.client) form.setFieldsValue({ client: project.client });
            }}
          />
        </Form.Item>
        <Form.Item name="client" label={t("Customer")}>
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            options={(meta?.clients || []).map(c => ({ value: c._id, label: c.name }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const SettingsModal = ({ open, settings, onClose, onSaved }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && settings) {
      form.setFieldsValue({
        ...settings,
        hourlyRate: centsToEuro(settings.hourlyRate),
        travelRate: centsToEuro(settings.travelRate)
      });
    }
  }, [open, settings]);

  const submit = async values => {
    setBusy(true);
    try {
      const res = await saveSettings({
        ...values,
        hourlyRate: euroToCents(values.hourlyRate),
        travelRate: euroToCents(values.travelRate)
      });
      Success(t("Saved"));
      onSaved(res.data.settings);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="work-reports-page"
      open={open}
      width={640}
      title={t("Work report settings")}
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Row gutter={[12, 0]}>
          <Col xs={12}>
            <Form.Item name="hourlyRate" label={t("Hourly rate")}>
              <InputNumber
                className="wr-full"
                min={0}
                precision={2}
                addonAfter="€"
                {...decimalInputProps(i18n.language)}
              />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="travelRate" label={t("Hourly rate for travel")}>
              <InputNumber
                className="wr-full"
                min={0}
                precision={2}
                addonAfter="€"
                {...decimalInputProps(i18n.language)}
              />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="rounding" label={t("Bill per started")}>
              <Select
                options={ROUNDING_OPTIONS.map(n => ({
                  value: n,
                  label: n ? t("{{n}} minutes", { n }) : t("Exact minutes")
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="taxRate" label={t("VAT rate")}>
              <Select options={[19, 7, 0].map(rate => ({ value: rate, label: `${rate} %` }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="showPrices"
              label={t("Show prices to the customer")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="emailCopy"
              label={t("Email the signed report to the customer")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="terms"
          label={t("Text above the signature")}
          extra={t("e.g. that the customer confirms the hours and materials listed.")}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={2000} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const WorkReports = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const history = useHistory();
  const isMobile = useIsMobile();
  const [meta, setMeta] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("all");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const labels = statusLabels(t);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listReports({ searchText, status: status === "all" ? undefined : status });
      setData(res.data.data);
      setSelected(prev => prev.filter(id => res.data.data.some(r => r._id === id)));
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);
  }, []);

  useEffect(() => {
    load();
  }, [searchText, status]);

  const selectedReports = data.filter(r => selected.includes(r._id));
  const selectedClients = new Set(selectedReports.map(r => r.client?._id || ""));
  const canInvoiceSelection =
    selectedReports.length > 0 && selectedClients.size === 1 && !selectedClients.has("");

  const invoice = async () => {
    setInvoicing(true);
    try {
      const res = await invoiceReports(selected);
      Success(t("Draft invoice created. Check it and send it from Invoices."));
      history.push("/user/invoices", { highlight: res.data.invoice._id });
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setInvoicing(false);
    }
  };

  const columns = [
    {
      title: t("Work report"),
      key: "number",
      mobile: "title",
      render: (_, item) => (
        <div>
          <Link to={`/user/work-reports/${item._id}`}>
            <Typography.Text strong>{item.number}</Typography.Text>
          </Link>{" "}
          <Typography.Text type="secondary">{formatDay(item.date)}</Typography.Text>
          <div className="wr-small wr-clamp">{item.workDescription || item.siteAddress}</div>
        </div>
      )
    },
    {
      title: t("Customer"),
      key: "client",
      mobile: "subtitle",
      render: (_, item) =>
        [item.client?.name, item.project?.projectName].filter(Boolean).join(" · ") || "—"
    },
    {
      title: t("Status"),
      key: "status",
      mobile: "badge",
      width: 150,
      render: (_, item) => (
        <Space size={[4, 4]} wrap>
          <Tag color={STATUS_COLORS[item.status]}>{labels[item.status]}</Tag>
          {item.status === "draft" && item.warningCount > 0 && (
            <Tag color="warning">{t("Please check")}</Tag>
          )}
          <PendingTag module="work-reports" record={item} />
        </Space>
      )
    },
    {
      title: t("Hours"),
      key: "hours",
      width: 90,
      align: "right",
      render: (_, item) =>
        formatMinutes((item.totals?.workMinutes || 0) + (item.totals?.travelMinutes || 0))
    },
    {
      title: t("Net"),
      key: "net",
      width: 120,
      align: "right",
      render: (_, item) => formatEuro(item.totals?.net, lng)
    }
  ];

  const selectable = record => BILLABLE.includes(record.status) && !record.invoice;
  const toggle = (id, checked) =>
    setSelected(prev => (checked ? [...prev, id] : prev.filter(key => key !== id)));
  // Phones show cards: selection is a checkbox in the card footer instead of a table column
  if (isMobile && meta?.can.invoice) {
    columns.push({
      title: "",
      key: "actions",
      mobile: "actions",
      render: (_, item) =>
        selectable(item) ? (
          <Checkbox
            checked={selected.includes(item._id)}
            onChange={event => toggle(item._id, event.target.checked)}
          >
            {t("Select for invoice")}
          </Checkbox>
        ) : null
    });
  }

  const isEmpty = !loading && !data.length && !searchText && status === "all";

  return (
    <div className="work-reports-page">
      <PageToolbar
        filters={
          <Space direction="vertical" className="wr-full">
            <SearchBarComp handleSearch={setSearchText} />
            <Segmented
              value={status}
              onChange={value => {
                setStatus(value);
                setSelected([]);
              }}
              options={[
                { value: "all", label: t("All") },
                { value: "draft", label: labels.draft },
                { value: "open", label: t("To invoice") },
                { value: "invoiced", label: labels.invoiced }
              ]}
            />
          </Space>
        }
        actions={
          <Space wrap>
            {meta?.can.settings && (
              <Button icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)}>
                {t("Settings")}
              </Button>
            )}
            {meta?.can.create && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
                {t("New work report")}
              </Button>
            )}
          </Space>
        }
      />

      {meta?.can.settings && meta.settings && !meta.settings.hourlyRate && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={t("Set your hourly rate once so new time lines are priced automatically.")}
          action={
            <Button size="small" onClick={() => setSettingsOpen(true)}>
              {t("Settings")}
            </Button>
          }
        />
      )}

      {selected.length > 0 && (
        <Alert
          className="mb-3 wr-selection"
          type={canInvoiceSelection ? "success" : "warning"}
          message={
            canInvoiceSelection
              ? t("{{n}} reports selected · {{amount}} net", {
                  n: selected.length,
                  amount: formatEuro(
                    selectedReports.reduce((sum, r) => sum + (r.totals?.net || 0), 0),
                    lng
                  )
                })
              : t("Select reports of one customer. Reports without a customer cannot be invoiced.")
          }
          action={
            <Space>
              <Button size="small" onClick={() => setSelected([])}>
                {t("Clear")}
              </Button>
              {meta?.can.invoice && (
                <Button
                  size="small"
                  type="primary"
                  icon={<FileTextOutlined />}
                  disabled={!canInvoiceSelection}
                  loading={invoicing}
                  onClick={invoice}
                >
                  {t("Create invoice")}
                </Button>
              )}
            </Space>
          }
        />
      )}

      {isEmpty ? (
        <Card>
          <Empty
            description={
              <>
                <Typography.Title level={5}>{t("Work reports signed on site")}</Typography.Title>
                <Typography.Paragraph type="secondary" className="wr-empty-text">
                  {t(
                    "Record hours, material and photos on the phone, let the customer sign on the screen or by link, and turn signed reports into an invoice."
                  )}
                </Typography.Paragraph>
              </>
            }
          >
            {meta?.can.create && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
                {t("New work report")}
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        (() => {
          const table = (
            <ResponsiveTable
              rowKey="_id"
              loading={loading}
              columns={columns}
              dataSource={data}
              mobileAvatar={false}
              rowSelection={
                meta?.can.invoice && !isMobile
                  ? {
                      selectedRowKeys: selected,
                      onChange: keys => setSelected(keys),
                      getCheckboxProps: record => ({ disabled: !selectable(record) })
                    }
                  : undefined
              }
              onRow={record => ({
                onDoubleClick: () => history.push(`/user/work-reports/${record._id}`)
              })}
              pagination={data.length > 50 ? { pageSize: 50 } : false}
              scroll={{ x: 720 }}
            />
          );
          return isMobile ? table : <Card styles={{ body: { padding: 0 } }}>{table}</Card>;
        })()
      )}

      <NewReportModal open={newOpen} meta={meta} onClose={() => setNewOpen(false)} />
      <SettingsModal
        open={settingsOpen}
        settings={meta?.settings}
        onClose={() => setSettingsOpen(false)}
        onSaved={settings => {
          setMeta(prev => ({ ...prev, settings }));
          setSettingsOpen(false);
        }}
      />
    </div>
  );
};

export default WorkReports;
